import {
  Component,
  OnInit,
  effect,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatToolbar } from '@angular/material/toolbar'
import { Router, RouterModule } from '@angular/router'
import { USER_ROLES } from '../../constants/auth'
import { AuthService } from '../../services/auth.service'
import { NavbarService } from '../../services/navbar.service'
import { CommonModule } from '@angular/common'
import { TranslationService } from '../../services/translation.service'
import { MarketplaceService } from '../../services/marketplace.service'

@Component({
  selector: 'app-navbar',
  imports: [MatToolbar, RouterModule, CommonModule],
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit {
  router = inject(Router)
  authService = inject(AuthService)
  navbarService = inject(NavbarService)
  translationService = inject(TranslationService)
  marketplace = inject(MarketplaceService)

  isMaster: boolean = false
  isAdmin: boolean = false
  isComplianceOfficer: boolean = false

  constructor() {
    // this acts as a subscription to the currentUserRole signal changes to update the flags below
    effect(() => {
      this.isMaster = this.authService.currentUserRole() === USER_ROLES.MASTER
      this.isAdmin = this.authService.currentUserRole() === USER_ROLES.ADMIN
      this.isComplianceOfficer =
        this.authService.currentUserRole() === USER_ROLES.COMPLIANCE_OFFICER
    })
  }

  ngOnInit(): void {
    this.marketplace.loadCart()
    const currentUserRole = this.authService.currentUserRole()
    if (currentUserRole === '') {
      const session = this.authService.getSessionFromLS()
      if (session?.user.role) {
        this.authService.currentUserRole.set(session.user.role)
      } else {
        this.authService.currentUserRole.set('Guest')
      }
    }

    this.navbarService.showNavBar()
  }

  /**
   * Compares a link to the current page's url
   * @param {string} link link to compare
   * @returns {boolean} Flag indicating if the link is the current page's
   */
  isActiveLink(link: string): boolean {
    return link == this.router.url
  }

  /**
   * Returns flag indicating if the current page is a user view
   * @returns {boolean} flag indicating if the current page is a user view
   */
  isUserView(): boolean {
    const urlSegments = this.router.url
      .split('/')
      .filter((segment) => segment.length)
    if (urlSegments.length == 3) {
      // we're expecting `/admin/users/create` for example, so want to verify that below
      const productSegment = urlSegments[1]
      return productSegment === 'users'
    }

    return false
  }

  /**
   * Returns flag indicating if the current page is a company view
   * @returns {boolean} flag indicating if the current page is a company view
   */
  isCompanyView(): boolean {
    const urlSegments = this.router.url
      .split('/')
      .filter((segment) => segment.length)
    if (urlSegments.length == 3) {
      // we're expecting `/admin/companies/create` for example, so want to verify that below
      const productSegment = urlSegments[1]
      return productSegment === 'companies'
    }

    return false
  }

  /**
   * Returns flag indicating if the current page is an ingredient page
   * @returns {boolean} flag indicating if the current page is an ingredient page
   */
  isIngredientPage(): boolean {
    let isIngredientChild = false
    const urlSegments = this.router.url
      .split('/')
      .filter((segment) => segment.length)
    if (urlSegments.length == 3) {
      // we're expecting `/ingredients/1/edit` for example, so want to verify that below
      const ingredientSegment = urlSegments[0]
      isIngredientChild = ingredientSegment === 'ingredients'
    }

    return (
      this.isActiveLink('/ingredients') ||
      this.isActiveLink('/ingredients/create') ||
      this.isActiveLink('/ingredients/import') ||
      isIngredientChild
    )
  }

  /**
   * Returns flag indicating if the current page is a reports page
   * @returns {boolean} flag indicating if the current page is a reports page
   */
  isReportsPage(): boolean {
    const urlSegments = this.router.url
      .split('/')
      .filter((segment) => segment.length)
    // Match `/admin/reports`
    if (urlSegments.length === 2 && urlSegments[1] === 'reports') {
      return true
    }
    // Match also `/reports/fpr/create` or `/reports/fpr/:id/edit`
    if (
      urlSegments[1] === 'reports' &&
      urlSegments[2] === 'fpr' &&
      (urlSegments[3] === 'create' ||
        (urlSegments.length >= 4 &&
          urlSegments[3] && // id is non-empty
          urlSegments[4] === 'edit'))
    ) {
      return true
    }
    return false
  }
}
